import type { ApprovalDetail } from '@myrewards/contracts';
import { useState, type ReactElement } from 'react';
import { Link, ToggleButton, ToggleButtonGroup, type Key } from 'react-aria-components';
import { movePerson, saveReview } from '../../api/ops';
import {
  Button,
  Card,
  cx,
  DataTable,
  formatCount,
  formatMoney,
  plural,
  StatusPill,
  useToast,
  type DataColumn,
} from '../../ui';
import { amountWords } from '../labels';
import { AmountInput, CountInput, Notice, TickBox } from '../shared';
import { ChangeStep, type ActingRefusal, type ChangeOpen } from './acting';
import { chargeWords, includedWords } from './parts';
import {
  actingOffer,
  editedTotals,
  editOf,
  editProblems,
  isDirty,
  movedBetween,
  names,
  paidFromLines,
  poolMovesWords,
  reviewPayload,
  reviewsPaaValue,
  splitWords,
  taaPoolChoices,
  withEdit,
  type Edits,
} from './presenter';
import { TaaSplitControl } from './TaaSplit';
import { useApprovalCommand } from './useApprovalCommand';
import s from '../Ops.module.css';

type Line = ApprovalDetail['lines'][number];

function PoolToggle({
  detail,
  line,
  onMove,
  isPending,
}: {
  detail: ApprovalDetail;
  line: Line;
  onMove: (poolId: string) => void;
  isPending: boolean;
}): ReactElement {
  const choices = taaPoolChoices(detail);
  if (choices.length < 2)
    return <span className={s.strong}>{choices[0]?.name ?? line.poolId}</span>;
  return (
    <ToggleButtonGroup
      aria-label={`Funding pool for ${line.personName}`}
      selectionMode="single"
      disallowEmptySelection
      isDisabled={isPending}
      selectedKeys={[line.poolId]}
      onSelectionChange={(keys: Set<Key>) => {
        const [key] = [...keys];
        if (typeof key === 'string' && key !== line.poolId) onMove(key);
      }}
      className={s.segmented}
    >
      {choices.map((pool) => (
        <ToggleButton key={pool.id} id={pool.id} className={s.segment}>
          {pool.name}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}

/**
 * X3 and R3: the people and amounts under review. Executives (and the recorder of an Executive
 * Discretionary decision) change amounts and who's included to match what was decided; a TAA's
 * people move wholly between the PAA/TAA pool and the sector pool. Never a split fraction.
 */
export function ReviewCard({
  detail,
  edits,
  onEdits,
  variant,
  onRefused,
}: {
  detail: ApprovalDetail;
  edits: Edits;
  onEdits: (edits: Edits) => void;
  variant: 'executive' | 'record';
  /** A change for an Executive refused because the page moved on: the page says why (APP-148). */
  onRefused?: ((refusal: ActingRefusal) => void) | undefined;
}): ReactElement {
  const toast = useToast();
  const { award, actions } = detail;
  // APP-148: a Stock Admin changes amounts only for a named Executive, through the step.
  const offer = actingOffer(detail);
  const forExecutive = offer?.role === 'executive' ? offer : null;
  const changesFor = !actions.edit && forExecutive?.actions.edit === true ? forExecutive : null;
  // R58: moving someone for an Executive goes through the same step; the own move stays beside it.
  const movesFor = forExecutive?.actions.movePerson === true ? forExecutive : null;
  const [change, setChange] = useState<ChangeOpen | null>(null);
  const canEdit = actions.edit || changesFor !== null;
  const type = award.type;
  const dirty = isDirty(detail, edits);
  const problems = dirty ? editProblems(detail, edits) : [];
  const totals = editedTotals(detail, edits);
  const anyOptions =
    (type === 'EDB' || type === 'SDB') && (canEdit || detail.lines.some((l) => l.options > 0));
  const save = useApprovalCommand(
    award.id,
    () => saveReview(award.id, reviewPayload(detail, edits)),
    () => {
      onEdits({});
      toast.show({
        tone: 'done',
        title: 'Changes saved',
        body: 'The budget check below uses them now.',
      });
    },
  );
  // This visit's pool moves, newest first (the record keeps every one, with who and why).
  const [moves, setMoves] = useState<string[]>([]);
  const move = useApprovalCommand(
    award.id,
    (vars: { line: Line; poolId: string }) =>
      movePerson(award.id, { version: award.version, lineId: vars.line.id, poolId: vars.poolId }),
    (next, vars) => {
      const moved = next.lines.find((l) => l.id === vars.line.id);
      const pool = taaPoolChoices(next).find((p) => p.id === moved?.poolId);
      if (moved && pool) {
        toast.show({ tone: 'done', title: `${moved.personName} is paid from ${pool.name} now` });
        setMoves((list) => [`You moved ${moved.personName} to the ${pool.name} pool.`, ...list]);
      }
    },
  );
  const set = (line: Line, change: Parameters<typeof withEdit>[2]): void => {
    onEdits(withEdit(edits, line, change));
  };

  const columns: DataColumn<Line>[] = [];
  if (variant === 'record')
    columns.push({
      id: 'approved',
      header: 'Approved',
      cell: (line) => {
        const edit = editOf(edits, line);
        return (
          <TickBox
            isSelected={edit.included}
            isDisabled={!canEdit}
            label={`${line.personName} approved`}
            onChange={(on) => {
              set(line, { included: on });
            }}
          >
            {edit.included ? 'Yes' : 'No'}
          </TickBox>
        );
      },
    });
  columns.push({
    id: 'person',
    header: 'Person',
    card: 'title',
    cell: (line) => {
      const included = editOf(edits, line).included;
      return (
        <>
          <span className={cx(s.strong, !included && s.struck)}>{line.personName}</span>
          <span className={s.sub}>
            {[line.jobTitle, line.coding ? (line.coding.costType === 'GA' ? 'G&A' : 'OH') : null]
              .filter((part): part is string => part !== null)
              .join(' · ') || 'No title listed'}
          </span>
          {!included && <StatusPill tone="problem" size="sm" label="Not approved" />}
        </>
      );
    },
  });
  if (variant === 'record')
    columns.push({
      id: 'request',
      header: 'On the request',
      numeric: true,
      cell: (line) => amountWords(line.cashCents, line.options),
    });
  // APP-151: an older PAA is changed by its total value; every PAA since by its cash (below).
  const byValue = reviewsPaaValue(detail);
  if (byValue) {
    columns.push({
      id: 'value',
      header: 'Total value',
      cell: (line) => {
        const edit = editOf(edits, line);
        return canEdit && edit.included ? (
          <AmountInput
            label={`Total value for ${line.personName}`}
            valueCents={edit.totalValueCents}
            onChange={(cents) => {
              set(line, { totalValueCents: cents });
            }}
          />
        ) : (
          formatMoney(edit.totalValueCents ?? 0)
        );
      },
    });
    columns.push({
      id: 'worked',
      header: 'Cash and options',
      cell: (line) =>
        editOf(edits, line).totalValueCents !== line.totalValueCents ? (
          <span className={s.muted}>Worked out when you save</span>
        ) : (
          amountWords(line.cashCents, line.options)
        ),
    });
  } else {
    columns.push({
      id: 'cash',
      header: type === 'PAA' ? 'Award amount' : variant === 'record' ? 'Approved cash' : 'Cash',
      cell: (line) => {
        const edit = editOf(edits, line);
        return canEdit && edit.included ? (
          <AmountInput
            label={`${type === 'PAA' ? 'Award amount' : 'Cash'} for ${line.personName}`}
            valueCents={edit.cashCents}
            onChange={(cents) => {
              set(line, { cashCents: cents });
            }}
          />
        ) : (
          formatMoney(edit.cashCents ?? 0)
        );
      },
    });
    // A PAA's options come on top of its cash, worked out when the change is saved.
    if (type === 'PAA')
      columns.push({
        id: 'worked',
        header: 'Options on top',
        cell: (line) =>
          editOf(edits, line).cashCents !== line.cashCents ? (
            <span className={s.muted}>Worked out when you save</span>
          ) : line.options === 0 ? (
            'None'
          ) : (
            formatCount(line.options)
          ),
      });
    if (anyOptions)
      columns.push({
        id: 'options',
        header: 'Approved options',
        cell: (line) => {
          const edit = editOf(edits, line);
          return canEdit && edit.included ? (
            <CountInput
              label={`Options for ${line.personName}`}
              value={edit.options}
              onChange={(n) => {
                set(line, { options: n });
              }}
            />
          ) : (
            formatCount(edit.options ?? 0)
          );
        },
      });
  }
  if (type === 'TAA')
    columns.push({
      id: 'pool',
      header: 'Funding pool',
      cell: (line) =>
        actions.movePerson && line.included ? (
          <PoolToggle
            detail={detail}
            line={line}
            isPending={move.isPending}
            onMove={(poolId) => {
              move.mutate({ line, poolId });
            }}
          />
        ) : (
          (taaPoolChoices(detail).find((p) => p.id === line.poolId)?.name ?? line.poolId)
        ),
    });
  if (variant === 'executive')
    columns.push({
      id: 'charge',
      header: 'Charge code',
      cell: (line) => <span className={s.code}>{chargeWords(line)}</span>,
    });
  if (variant === 'executive' && canEdit)
    columns.push({
      id: 'remove',
      header: 'Remove',
      card: 'action',
      cell: (line) => {
        const included = editOf(edits, line).included;
        return (
          <Button
            variant="ghost"
            size="sm"
            aria-label={`${included ? 'Remove' : 'Put back'} ${line.personName}`}
            onPress={() => {
              set(line, { included: !included });
            }}
          >
            {included ? 'Remove' : 'Put back'}
          </Button>
        );
      },
    });

  const paid = paidFromLines(detail, edits);
  return (
    <Card
      title={variant === 'record' ? 'What was approved' : 'People, amounts and funding'}
      help={{
        shows:
          variant === 'record'
            ? 'Everyone on the request with what they asked for. Uncheck anyone the Executives didn’t approve, and change an amount if they did.'
            : 'Everyone on the award with their amounts, the pool that pays each person and their charge code.',
        canDo: canEdit
          ? 'Change amounts or who is included to match the decision, then save. Every change is logged with the person it touches.'
          : 'Read it through. Only the person deciding can change it.',
      }}
      aside={
        <span className={s.muted}>
          {variant === 'record'
            ? `${includedWords(totals.people, totals.of)} · ${formatMoney(totals.cashCents)}`
            : canEdit
              ? 'Edit any amount or remove a person to match the decision'
              : peopleCount(totals.people)}
        </span>
      }
    >
      {type === 'TAA' && (
        <div className={s.paidBox}>
          {(actions.changeTaaSplit || forExecutive?.actions.changeTaaSplit === true) && (
            <TaaSplitControl
              detail={detail}
              isDisabled={dirty}
              onRefused={onRefused}
              onChanged={(before, after, share) => {
                const moved = movedBetween(before, after);
                const words = `Split changed to ${splitWords(share)} by person: ${
                  moved.length === 0
                    ? 'no one needed to move.'
                    : `${moved.map((g) => `the app moved ${names(g.names)} to the ${g.pool.name} pool`).join('; ')}.`
                }`;
                setMoves((list) => [words, ...list]);
                toast.show({ tone: 'done', title: 'Split changed', body: words });
              }}
            />
          )}
          <p className={s.strong}>Paid from</p>
          <ul className={s.paidFrom}>
            {/* Both pools, even one paying no one: a split can put everyone on one pool. */}
            {taaPoolChoices(detail).map((pool) => {
              const row = paid.find((r) => r.pool.id === pool.id);
              return (
                <li key={pool.id} className={s.paidRow}>
                  <span>
                    <span className={s.strong}>{pool.name}</span>
                    <span className={s.muted}>
                      {' '}
                      · {plural(row?.people ?? 0, 'person', 'people')}
                    </span>
                  </span>
                  <span className={s.strong}>{formatMoney(row?.cashCents ?? 0)}</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
      <DataTable
        caption={variant === 'record' ? 'What was approved' : 'People, amounts and funding'}
        rows={detail.lines}
        rowKey={(line) => line.id}
        columns={columns}
        minWidth={variant === 'record' ? 640 : 820}
        cards
        empty={{
          title: 'No one on this award.',
          body: 'People come from the entry; add them there.',
        }}
      />
      <p className={s.totalRow}>
        <span>{includedWords(totals.people, totals.of)}</span>
        <span className={s.strong}>
          Total{' '}
          {byValue
            ? `${formatMoney(totals.valueCents)} value`
            : type === 'PAA' && dirty
              ? `${formatMoney(totals.cashCents)} cash, options worked out when you save`
              : amountWords(totals.cashCents, totals.options)}
        </span>
      </p>
      {type === 'TAA' && (
        <section className={s.poolMoves} aria-label="Pool moves">
          <p className={cx('eyebrow', s.eyebrowMuted)}>Pool moves</p>
          <ul className={s.poolMovesList}>
            {moves.map((m, i) => (
              <li key={`${String(i)}-${m}`} className={s.strong}>
                {m}
              </li>
            ))}
            {poolMovesWords(detail) !== null && <li>{poolMovesWords(detail)}</li>}
          </ul>
          <p className={s.muted}>
            Each person is paid wholly from one pool, and their charge code follows the pool. Every
            move is logged on{' '}
            <Link href={`/awards/${award.id}`} className={s.inlineLink}>
              the award’s record
            </Link>
            . The default split is an Executive setting on Budget pools.
          </p>
          {/* One "Change for an Executive…" at a time: with unsaved amounts, it's the save bar's. */}
          {movesFor !== null && !dirty && (
            <div className={s.actionsRow}>
              <Button
                variant="ghost"
                size="sm"
                onPress={() => {
                  setChange({ kind: 'move', offer: movesFor });
                }}
              >
                Change for an Executive…
              </Button>
              <span className={s.muted}>To move someone between the pools for an Executive.</span>
            </div>
          )}
        </section>
      )}
      {move.error !== null && <Notice tone="problem">{move.error}</Notice>}
      {dirty && (
        <div className={s.saveBar}>
          <Notice tone="waiting">
            You have unsaved changes. Save them to see the amounts and budget they give.
          </Notice>
          {problems.map((problem) => (
            <Notice key={problem} tone="problem">
              {problem}
            </Notice>
          ))}
          <div className={s.actionsRow}>
            {changesFor === null ? (
              <Button
                variant="navy"
                size="sm"
                isDisabled={problems.length > 0}
                isPending={save.isPending}
                onPress={() => {
                  save.mutate(undefined);
                }}
              >
                Save changes
              </Button>
            ) : (
              <Button
                variant="navy"
                size="sm"
                isDisabled={problems.length > 0}
                onPress={() => {
                  setChange({ kind: 'amounts', edits, offer: changesFor });
                }}
              >
                Change for an Executive…
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onPress={() => {
                onEdits({});
              }}
            >
              Undo changes
            </Button>
          </div>
        </div>
      )}
      {save.error !== null && <Notice tone="problem">{save.error}</Notice>}
      {change !== null && (
        <ChangeStep
          detail={detail}
          open={change}
          onRefused={onRefused}
          onChanged={(next, person) => {
            if (change.kind === 'move')
              for (const group of movedBetween(detail, next))
                setMoves((list) => [
                  `You moved ${names(group.names)} to the ${group.pool.name} pool for ${person.name}.`,
                  ...list,
                ]);
            onEdits({});
            setChange(null);
          }}
          onClose={() => {
            setChange(null);
          }}
        />
      )}
    </Card>
  );
}

function peopleCount(people: number): string {
  return plural(people, 'person', 'people');
}
